import {
  layoutModeChanged,
  localStreamCollapsed,
  localStreamExpanded,
} from 'features/layout/features/config/configSlice';
import {
  bulkCloseContentRequested,
  signalingBulkContentClosed,
  signalingWhiteboardChanged,
  signalingWhiteboardClosed,
  signalingWhiteboardCursorUpdated,
  signalingWhiteboardOpened,
  whiteboardClosed,
  whiteboardOpened,
  whiteboardCreated,
  signalingWhiteboardCreated,
  signalingWhiteboardAlreadyOpened,
  contentPushed,
  contentLocked,
  signalingContentPushed,
  contentUnlocked,
  navigateToLockedContent,
} from 'features/layout/features/content/actions';
import {
  gridPanelClosed,
  gridPanelOpened,
  gridPanelTabChanged,
  gridPanelVoided,
} from 'features/layout/features/content/contentSlice';
import { onGridPanelClosedSaga } from 'features/layout/features/content/sagas/onGridPanelClosedSaga';
import { onGridPanelOpenedSaga } from 'features/layout/features/content/sagas/onGridPanelOpenedSaga';
import { onGridPanelVoidedSaga } from 'features/layout/features/content/sagas/onGridPanelVoidedSaga';
import { onGridPanelTabChangedSaga } from 'features/layout/features/content/sagas/onGridPanelTabChangedSaga';
import { reconcileScreenshareVisibilitySaga } from 'features/screenshare/sagas/reconcileScreenshareVisibilitySaga';
import { onPinControlToggledSaga } from 'features/layout/features/content/sagas/onPinControlToggledSaga';
import { onContentPushedSaga } from 'features/layout/features/content/sagas/onContentPushedSaga';
import { pushContentSaga } from 'features/layout/features/content/sagas/pushContentSaga';
import { lockContentSaga } from 'features/layout/features/content/sagas/lockContentSaga';
import { unlockContentSaga } from 'features/layout/features/content/sagas/unlockContentSaga';
import { navigateToLockedContentSaga } from 'features/layout/features/content/sagas/navigateToLockedContentSaga';
import { dimensionsChanged } from 'features/layout/features/dimensions/dimensionsSlice';
import { orderedStreamDeleted } from 'features/layout/features/order/orderSlice';
import { paginationUpdated } from 'features/layout/features/pagination/paginationSlice';
import { localStreamCollapseSaga } from 'features/layout/sagas/localStreamCollapseSaga';
import { onCustomControlTriggeredSaga } from 'features/layout/sagas/onCustomControlTriggeredSaga';
import { onDimensionsChangedSaga } from 'features/layout/sagas/onDimensionsChangedSaga';
import { onLayoutModeChangedSaga } from 'features/layout/sagas/onLayoutModeChangedSaga';
import { onOrderedStreamDeletedSaga } from 'features/layout/sagas/onOrderedStreamDeletedSaga';
import { onStreamMaximizedSaga } from 'features/layout/sagas/onStreamMaximizedSaga';
import { onStreamMinimizedSaga } from 'features/layout/sagas/onStreamMinimizedSaga';
import { onWhiteboardChangedSaga } from 'features/layout/sagas/onWhiteboardChangedSaga';
import { onWhiteboardCursorPositionChangedSaga } from 'features/layout/sagas/onWhiteboardCursorPositionChangedSaga';
import { debounce, takeEvery, takeLatest } from 'redux-saga/effects';
import { onPaginationSaga } from 'features/layout/sagas/onPaginationSaga';
import { onOrderChangedSaga } from 'features/layout/sagas/onOrderChangedSaga';
import {
  customControlTriggered,
  orderChanged,
  pinControlToggled,
  streamMaximized,
  streamMinimized,
} from 'features/layout/actions';
import { signalingUserJoined } from 'features/users/actions';
import { onUserJoinedSaga } from 'features/layout/sagas/onUserJoinedSaga';
import { onBroadcastEndSaga } from 'features/layout/sagas/onBroadcastEndSaga';
import { activeSpeakerChanged, speakerStoppedTalking } from 'features/streaming/actions';
import { onActiveSpeakerChangedSaga } from 'features/layout/sagas/onActiveSpeakerChangedSaga';
import { onSpeakerStoppedTalkingSaga } from 'features/layout/sagas/onSpeakerStoppedTalkingSaga';
import { targetTilesCountSet } from 'features/dev-mode/devModeSlice';
import { debugTilesChangeSaga } from 'features/layout/sagas/debugTilesChangeSaga';
import { minimizeHiddenRoleStageSaga } from 'features/layout/sagas/minimizeHiddenRoleStageSaga';
import { changeRole, userLeft } from 'features/users/usersSlice';
import { signalingRoomJoined } from 'features/room/actions';
import { prepareRoomLayoutSaga } from 'features/layout/sagas/prepareRoomLayoutSaga';
import { onLocalStreamExpandedSaga } from 'features/layout/sagas/onLocalStreamExpandedSaga';
import { openWhiteboardSaga } from './openWhiteboardSaga';
import { onWhiteboardOpenedSaga } from './onWhiteboardOpenedSaga';
import { closeWhiteboardSaga } from './closeWhiteboardSaga';
import { onWhiteboardClosedSaga } from './onWhiteboardClosedSaga';
import { bulkCloseContentSaga } from './bulkCloseContentSaga';
import { onBulkContentClosedSaga } from './onBulkContentClosedSaga';
import { createWhiteboardSaga } from './createWhiteboardSaga';
import { onWhiteboardCreatedSaga } from './onWhiteboardCreatedSaga';
import { onWhiteboardAlreadyOpenedSaga } from './onWhiteboardAlreadyOpenedSaga';

export default function* layoutWatcher() {
  yield takeEvery(signalingRoomJoined, prepareRoomLayoutSaga);

  yield takeEvery(dimensionsChanged, onDimensionsChangedSaga);

  // order stuff
  yield takeEvery(orderChanged, onOrderChangedSaga);

  yield takeEvery(signalingUserJoined, onUserJoinedSaga);

  // @TODO: this would be a good place to do other sorts of cleanup (like for maximized tile);
  yield takeEvery(userLeft, onBroadcastEndSaga);

  yield takeEvery(changeRole, minimizeHiddenRoleStageSaga);

  yield takeEvery(activeSpeakerChanged, onActiveSpeakerChangedSaga);
  yield takeEvery(speakerStoppedTalking, onSpeakerStoppedTalkingSaga);

  yield takeEvery(streamMaximized, onStreamMaximizedSaga);

  yield takeEvery(streamMinimized, onStreamMinimizedSaga);

  yield debounce(400, paginationUpdated, onPaginationSaga);

  yield takeEvery(localStreamCollapsed, localStreamCollapseSaga);

  yield takeEvery(layoutModeChanged, onLayoutModeChangedSaga);

  yield takeEvery(orderedStreamDeleted, onOrderedStreamDeletedSaga);

  yield takeEvery(pinControlToggled, onPinControlToggledSaga);

  // support debug tiles
  yield takeEvery(targetTilesCountSet, debugTilesChangeSaga);

  yield takeEvery(localStreamExpanded, onLocalStreamExpandedSaga);

  yield takeEvery(whiteboardOpened, openWhiteboardSaga);
  yield takeEvery(whiteboardClosed, closeWhiteboardSaga);
  yield takeEvery(whiteboardCreated, createWhiteboardSaga);

  yield takeEvery(bulkCloseContentRequested, bulkCloseContentSaga);
  yield takeEvery(signalingBulkContentClosed, onBulkContentClosedSaga);

  yield takeEvery(signalingWhiteboardOpened, onWhiteboardOpenedSaga);
  yield takeEvery(signalingWhiteboardClosed, onWhiteboardClosedSaga);
  yield takeEvery(signalingWhiteboardCreated, onWhiteboardCreatedSaga);
  yield takeEvery(signalingWhiteboardChanged, onWhiteboardChangedSaga);
  yield takeEvery(signalingWhiteboardCursorUpdated, onWhiteboardCursorPositionChangedSaga);
  yield takeEvery(signalingWhiteboardAlreadyOpened, onWhiteboardAlreadyOpenedSaga);

  yield takeEvery(gridPanelOpened, onGridPanelOpenedSaga);
  yield takeEvery(gridPanelClosed, onGridPanelClosedSaga);
  yield takeEvery(gridPanelVoided, onGridPanelVoidedSaga);
  yield takeEvery(gridPanelTabChanged, onGridPanelTabChangedSaga);

  // Stop/resume the remote screenshare as the user moves between the screenshare panel,
  // the stage (maximize/minimize), and other content (whiteboard, files, external streams).
  // takeLatest so rapid switching reconciles to the final visible state.
  yield takeLatest(
    [
      gridPanelOpened,
      gridPanelClosed,
      gridPanelVoided,
      gridPanelTabChanged,
      streamMaximized,
      streamMinimized,
    ],
    reconcileScreenshareVisibilitySaga
  );

  yield takeEvery(contentPushed, pushContentSaga);
  yield takeEvery(contentLocked, lockContentSaga);
  yield takeEvery(signalingContentPushed, onContentPushedSaga);

  yield takeEvery(contentUnlocked, unlockContentSaga);
  yield takeEvery(navigateToLockedContent, navigateToLockedContentSaga);

  yield takeEvery(customControlTriggered, onCustomControlTriggeredSaga);
}
